import { GetServerSideProps } from 'next';
import { getApolloClient } from '@/lib/ApolloClient';
import GET_JOB_DETAILS from '@/API/getJobDetails';
import { FeatureFlags, LDTargetAttributes, PageTemplate, GAMTargetingRules } from '@/types/common';
import { getGAMVerticalShortName } from '@/utils/GoogleAdsManager/getGAMVerticalShortName';
import { UI_FEATURE_FLAGS } from '@/constants';
import {
  getSegmentationJobDetails,
  getSegmentationJobDetailsVariables,
} from '../../../../__generated__/getSegmentationJobDetails';
import getDeviceCharacteristics from '../../../../utils/getDeviceCharacteristics';
import reqHeaderToEntriesObj from '../../../../utils/reqHeaderToEntriesObj';

const isLocal = process.env.NODE_ENV === 'development';

const getJobDetailData: GetServerSideProps = async ({ query, req, res }) => {
  const startPageGSSP = performance.now();
  const apolloOptions = {
    logError: req.log.error.bind(req.log),
    logInfo: req.log.info.bind(req.log),
  };
  const startApolloClient = performance.now();
  const apolloClient = getApolloClient(apolloOptions);
  const stopApolloClient = performance.now();
  const seoSegmentId = `/job/${query.vertical}/${query.state}/${query.city}/${query.jobId}`;
  const isSeniorCare = Boolean(query.vertical?.toString().includes('seniorcare'));
  const isChildCare = Boolean(query.vertical?.toString().includes('childcare'));
  const isPetCare = Boolean(query.vertical?.toString().includes('petcare'));
  const isHousekeeping = Boolean(query.vertical?.toString().includes('housekeeping'));
  const isTutoring = Boolean(query.vertical?.toString().includes('tutoring'));
  const userAgent = req.headers['user-agent'] || '';
  const deviceCharHeader = req.headers['x-akamai-device-characteristics']?.toString() || '';
  const deviceChar = isLocal
    ? getDeviceCharacteristics(userAgent, deviceCharHeader)
    : reqHeaderToEntriesObj(deviceCharHeader, ';');
  const startGetPageData = performance.now();
  let data;
  try {
    data = await apolloClient.query<getSegmentationJobDetails, getSegmentationJobDetailsVariables>({
      query: GET_JOB_DETAILS,
      variables: {
        seoSegmentId,
        isSeniorCare,
        isChildCare,
        isPetCare,
        isHousekeeping,
        isTutoring,
      },
    });
    if (!data.data) {
      req.log.error({
        event: 'SeoJobDetailPageLoadError',
        seoSegmentId,
        userAgent,
        query,
        res,
        deviceChar,
        data,
      });
      return {
        notFound: true,
      };
    }
  } catch (err) {
    req.log.error({
      event: 'SeoJobDetailPageLoadError',
      seoSegmentId,
      userAgent,
      query,
      res,
      deviceChar,
      err,
    });
    return {
      notFound: true,
    };
  }
  const stopGetPageData = performance.now();

  req.log.info({
    event: 'SeoJobDetailPageLoadSuccess',
    seoSegmentId,
    userAgent,
    deviceChar,
    query,
    res,
  });

  const ldClientFlags: FeatureFlags = {};
  const { ldClient, ldUser } = req.careContext;
  const ldTargetAttributes: LDTargetAttributes = {
    seoSegmentId,
    deviceChar,
    pageTemplate: PageTemplate.JOB_PROFILE,
  };

  if (ldClient && ldUser) {
    ldUser.custom = {
      ...ldUser.custom,
      seoSegmentId,
      pageTemplate: PageTemplate.JOB_PROFILE,
    };
    const clientFlagPromises = Object.values(UI_FEATURE_FLAGS)
      .filter((flag) => flag.condition(ldTargetAttributes))
      .map(async (flag) => {
        ldClientFlags[flag.id] = await ldClient.variationDetail(flag.id, ldUser, undefined);
      });

    await Promise.all(clientFlagPromises);
  }

  const additionalGAMRules: Partial<GAMTargetingRules> = {
    contType: 'provider',
    vertical: getGAMVerticalShortName(
      data.data?.getSEOSegmentationJobDetails?.seoSegmentationJob?.serviceId ?? ''
    ),
  };

  const stopPageGSSP = performance.now();
  req.careContext.serverTimings.pageGSSP = stopPageGSSP - startPageGSSP;
  req.careContext.serverTimings.apolloClient = stopApolloClient - startApolloClient;
  req.careContext.serverTimings.pageData = stopGetPageData - startGetPageData;
  return {
    props: {
      serviceShortName: query.vertical,
      data: data.data,
      testName: query.testIDToFireTestExposureEvent || null,
      deviceChar,
      ldClientFlags,
      additionalGAMRules,
    },
  };
};
export default getJobDetailData;
